import React, { useState, useEffect, useMemo } from "react";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { ACTIONS } from "../lib/actionSchemas";
import PhotoUpload from "./PhotoUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";

export default function RecordDialog({ action, open, onOpenChange, prefillTyreId, siteId, onDone }) {
  const cfg = ACTIONS[action];
  const { meta } = useAuth();
  const [form, setForm] = useState({});
  const [tyres, setTyres] = useState([]);
  const [saving, setSaving] = useState(false);

  const needsTyres = useMemo(() => cfg?.fields.some((f) => f.type === "tyre"), [cfg]);

  useEffect(() => {
    if (!open) return;
    setForm(prefillTyreId ? { tyre_id: prefillTyreId } : {});
    if (needsTyres) api.get("/tyres").then(({ data }) => setTyres(data));
  }, [open, prefillTyreId, action, needsTyres]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isTruck = (size) => (meta?.truck_sizes || []).includes(size);

  // Reset position when size/axles change so a stale position can't be submitted.
  useEffect(() => { setForm((f) => (f.position ? { ...f, position: undefined } : f)); }, [form.size, form.axles]);

  const visible = (f) => {
    if (f.showIf && form[f.showIf.field] !== f.showIf.value) return false;
    if (f.showIfTruckSize && !isTruck(form.size)) return false;
    if (f.showIfMakeModel && !(meta?.make_model_sizes || []).includes(form.size)) return false;
    return true;
  };

  const submit = async () => {
    for (const f of cfg.fields) {
      if (visible(f) && f.required && (form[f.name] === undefined || form[f.name] === "")) {
        toast.error(`${f.label} is required`); return;
      }
    }
    if (cfg.needsSite && !siteId) { toast.error("Select your mine site first"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (cfg.needsSite || siteId) payload.mine_site_id = siteId;
      ["photos", "photos_before", "photos_after"].forEach((k) => {
        if (payload[k]) payload[k] = payload[k].map((p) => ({ id: p.id, url: p.url }));
      });
      await api.post(cfg.endpoint, payload);
      toast.success(`${cfg.title} saved`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  if (!cfg) return null;

  const tyreOptions = (f) => tyres.filter(f.tyreFilter || (() => true));

  const renderField = (f) => {
    const val = form[f.name];
    switch (f.type) {
      case "tyre":
        return (
          <Select value={val || ""} onValueChange={(v) => set(f.name, v)}>
            <SelectTrigger data-testid={`field-${f.name}`} className="bg-zinc-950"><SelectValue placeholder="Select tyre" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {tyreOptions(f).map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.serial_number} — {t.size} ({t.status})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case "select": {
        let opts;
        if (f.optionsKey === "positions") {
          opts = isTruck(form.size)
            ? (meta?.positions_by_axles?.[form.axles] || [])
            : (meta?.positions_by_size?.[form.size] || meta?.positions || []);
        } else if (f.options) {
          opts = f.options;
        } else {
          opts = meta?.[f.optionsKey] || [];
        }
        const norm = opts.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
        return (
          <Select value={val || ""} onValueChange={(v) => set(f.name, v)}>
            <SelectTrigger data-testid={`field-${f.name}`} className="bg-zinc-950"><SelectValue placeholder={`Select ${f.label}`} /></SelectTrigger>
            <SelectContent className="max-h-72">
              {norm.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
            </SelectContent>
          </Select>
        );
      }
      case "textarea":
        return <Textarea data-testid={`field-${f.name}`} value={val || ""} onChange={(e) => set(f.name, e.target.value)} className="bg-zinc-950" />;
      case "date":
        return <Input data-testid={`field-${f.name}`} type="date" value={val || ""} onChange={(e) => set(f.name, e.target.value)} className="bg-zinc-950" />;
      case "checkbox":
        return (
          <div className="flex items-center gap-2 pt-2">
            <Checkbox data-testid={`field-${f.name}`} checked={!!val} onCheckedChange={(c) => set(f.name, !!c)} />
            <span className="text-sm text-zinc-400">{f.label}</span>
          </div>
        );
      case "photos":
        return <PhotoUpload testid={`field-${f.name}`} value={val || []} onChange={(v) => set(f.name, v)} />;
      default:
        return <Input data-testid={`field-${f.name}`} type={f.type === "number" ? "number" : "text"}
          value={val ?? ""} onChange={(e) => set(f.name, f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} className="bg-zinc-950" />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid={`dialog-${action}`}>
        <DialogHeader><DialogTitle className="font-display">{cfg.title}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {cfg.fields.filter(visible).map((f) => (
            <div key={f.name} className={f.half ? "sm:col-span-1" : "sm:col-span-2"}>
              {f.type !== "checkbox" && <Label className="text-xs text-zinc-400">{f.label}{f.required && " *"}</Label>}
              <div className="mt-1">{renderField(f)}</div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="dialog-submit" className="bg-amber-500 font-bold text-zinc-950 hover:bg-amber-400">
            {saving ? "Saving..." : cfg.title}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
